import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { ScopeBranchView } from "../api/access";
import type { ApiError } from "../api/client";
import { structureApi, type PositionNode } from "../api/structure";
import { PositionKindIcon } from "./PositionKindIcon";
import { PositionTreePicker } from "./PositionTreePicker";
import { useToast } from "./Toast";
import Ic from "./icons.jsx";

export type ScopeMode = "all" | "branches";

/**
 * "Opseg izmena strukture" on "Pristup korisnika" (sprint 7 §12): the whole structure, or only the
 * chosen branches — each a position with everything below it — for the functions that follow the
 * scope (`scopedFunctions`, from the catalogue). Reading the tree is never narrowed. "Samo odabrane
 * grane" without a branch would mean the whole structure, so the page refuses to save it.
 */
export function StructureScopeSection({
  mode, branches, readOnly, scopedFunctions, onModeChange, onAdd, onRemove,
}: {
  mode: ScopeMode;
  branches: ScopeBranchView[];
  readOnly: boolean;
  /** Display names of the functions the scope narrows. */
  scopedFunctions: string[];
  onModeChange: (mode: ScopeMode) => void;
  onAdd: (branch: ScopeBranchView) => void;
  onRemove: (id: string) => void;
}) {
  const { t } = useTranslation();
  const toast = useToast();
  const [adding, setAdding] = useState(false);
  const [busy, setBusy] = useState(false);

  /** The picker gives the node; the card brings its path for the list. */
  async function pick(node: PositionNode) {
    if (busy) return;
    if (branches.some((b) => b.id === node.id)) { setAdding(false); return; }
    setBusy(true);
    try {
      const d = await structureApi.get(node.id);
      onAdd({ id: d.id, code: d.code, name: d.name, kind: d.kind, path: d.path, deleted: false });
      setAdding(false);
    } catch (err) {
      toast.error((err as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card" style={{ marginBottom: 16 }}>
      <div className="card-h">
        <div>
          <h3>{t("userAccess.scope.title", "Opseg izmena strukture")}</h3>
          <div className="sub">
            {scopedFunctions.length > 0
              ? t("userAccess.scope.sub", "Gde važe: {{functions}}. Pregled strukture uvek obuhvata celo drvo.", { functions: scopedFunctions.join(", ") })
              : t("userAccess.scope.subGeneric", "Gde važe funkcije za izmenu strukture i sastavnice. Pregled strukture uvek obuhvata celo drvo.")}
          </div>
        </div>
      </div>
      <div className="card-b" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <label className="check-row">
          <input type="radio" name="structure-scope" checked={mode === "all"} disabled={readOnly} onChange={() => onModeChange("all")} />
          {t("userAccess.scope.all", "Cela struktura")}
        </label>
        <label className="check-row">
          <input type="radio" name="structure-scope" checked={mode === "branches"} disabled={readOnly} onChange={() => onModeChange("branches")} />
          {t("userAccess.scope.branches", "Samo odabrane grane (svaka sa svime ispod nje)")}
        </label>

        {mode === "branches" && (
          <>
            {branches.length === 0 ? (
              <span className="filter-row-hint is-err" role="alert">
                {t("userAccess.scope.none", "Izaberite bar jednu granu — bez grana korisnik menja celu strukturu.")}
              </span>
            ) : (
              <div className="role-list">
                {branches.map((b) => (
                  <div key={b.id} className="check-row">
                    <PositionKindIcon kind={b.kind} />
                    <span className="role-list-text">
                      <b>{b.name}</b>
                      {b.path.length > 0 && <span className="sub">{b.path.map((p) => p.name).join(" / ")}</span>}
                      {b.code && <span className="code">{b.code}</span>}
                    </span>
                    {b.deleted && <span className="tag err dot">{t("structure.deletedShort", "Obrisana")}</span>}
                    {!readOnly && (
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={t("userAccess.scope.remove", "Ukloni granu „{{name}}“", { name: b.name })}
                        title={t("userAccess.scope.remove", "Ukloni granu „{{name}}“", { name: b.name })}
                        onClick={() => onRemove(b.id)}
                      >
                        <Ic.x />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
            {!readOnly && (adding ? (
              <div className="field">
                <span>{t("userAccess.scope.pick", "Izaberite granu (fabriku, pogon, liniju…)")}</span>
                <PositionTreePicker value={null} onChange={(n) => { void pick(n); }} />
                <div>
                  <button type="button" className="btn sm" onClick={() => setAdding(false)}>{t("action.cancel", "Otkaži")}</button>
                </div>
              </div>
            ) : (
              <div>
                <button type="button" className="btn sm" onClick={() => setAdding(true)}>
                  <Ic.plus /> {t("userAccess.scope.add", "Dodaj granu")}
                </button>
              </div>
            ))}
          </>
        )}
      </div>
    </section>
  );
}
